// Roll-cage estimate for Large picks — the device-side twin of
// Portal-BE/app/warehouse/services/picking-rollcages.js. Keep the two
// in step; both inputs — cage capacity and utilisation — are
// per-warehouse settings the BE owns and ships on the wave header.
// This file owns nothing but the arithmetic.
//
// Why it runs on the device at all: the estimate is scoped to the ORDER
// the picker is on, and a Large wave's line set moves underneath it —
// the JIT lookahead appends the next order's lines over SSE without
// ever refreshing the wave header, and local-first owns its line set
// between reconciles. Computing from `lines` means the number is
// always about the lines actually on screen. A pre-summed server total
// would go stale the moment either of those fired.
//
//     cages = round( order_cbm / (roll_cage_cbm * utilisation), 0.1 )
//
// Unmeasured products contribute ZERO volume — the same safe-fail rule
// the Large classifier uses, so the estimate can never disagree with
// the classification that made this a Large order. That makes it a
// FLOOR, which is why missingDimUnits is returned alongside and always
// shown: an estimate quietly computed off half an order is worse than
// no estimate.
import type { ActiveWaveLine } from "../../hooks/useActiveWave";

// products.length_cm & co are NUMERIC — node-postgres serialises them
// as strings, so everything here goes through Number().
const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
};

const CM3_PER_CBM = 1_000_000;

export interface RollCageEstimate {
    /**
     * Cage-fulls, to the nearest TENTH of a cage — 0.3, 1.2. Not a
     * whole-number count: real Large orders run a fraction of a cage, so
     * ceiling to whole cages answered "1" for every order and told the
     * picker nothing. Floored at 0.1 for any measurable volume.
     * Exactly 0 = nothing measurable; render as unknown, never as "none".
     */
    cages: number;
    /** Summed volume of the measured lines, CBM. */
    totalCbm: number;
    /** UNITS the estimate could not see, because their product has no size. */
    missingDimUnits: number;
    /** Usable volume of one cage after utilisation, CBM. Null = estimate off. */
    cageCbm: number | null;
}

/** All three sides present and positive. A zero side is as unusable as a null one. */
const hasDims = (line: ActiveWaveLine): boolean => {
    const l = num(line.length_cm), w = num(line.width_cm), h = num(line.height_cm);
    return l > 0 && w > 0 && h > 0;
};

// Units still bound for a cage. 'missing' lines are excluded: those
// units are either short or already re-slotted onto a child line, and
// counting both would inflate the estimate at the worst moment.
const cageableQty = (line: ActiveWaveLine): number => {
    if (line.status === "missing") return 0;
    const q = num(line.qty_required);
    return q > 0 ? q : 0;
};

/**
 * Estimate for one order's worth of lines.
 *
 * Both come off the wave header: `utilisationPct` is
 * settings.picking_roll_cage_utilisation_pct and `cageCbm` is
 * settings.picking_roll_cage_cbm, already coalesced BE-side to the
 * standard cage when the warehouse never set one. Either arriving
 * absent/invalid means the estimate is off for this warehouse — the
 * caller renders nothing rather than guessing a capacity.
 */
export const estimateRollCages = (
    lines: ActiveWaveLine[],
    utilisationPct: number | string | null | undefined,
    cageCbm: number | string | null | undefined,
): RollCageEstimate | null => {
    const pct = num(utilisationPct);
    const capacity = num(cageCbm);
    if (!(pct > 0) || pct > 100 || !(capacity > 0)) return null;
    // No lines is never a real answer — an order always has some. It
    // means the device doesn't hold this order's lines yet (the anchor
    // window before the first append, or a legacy non-local-first
    // screen whose line set predates a JIT-appended order). Showing
    // nothing is right; "roll cages unknown" would blame the catalogue
    // for a gap that isn't there.
    if (lines.length === 0) return null;
    const usable = capacity * (pct / 100);

    let cm3 = 0;
    let missingDimUnits = 0;
    for (const line of lines) {
        const qty = cageableQty(line);
        if (qty === 0) continue;
        if (hasDims(line)) {
            cm3 += qty * num(line.length_cm) * num(line.width_cm) * num(line.height_cm);
        } else {
            missingDimUnits += qty;
        }
    }
    const totalCbm = cm3 / CM3_PER_CBM;
    return {
        // Nearest tenth, floored at 0.1 so an order with real goods in
        // it never reads 0.0. Mirrors rollCagesFor on the BE.
        cages: totalCbm > 0 ? Math.max(0.1, Math.round((totalCbm / usable) * 10) / 10) : 0,
        totalCbm,
        missingDimUnits,
        cageCbm: usable,
    };
};

/** Lines belonging to one order. Large waves carry an exact order_id per line. */
export const linesForOrder = (lines: ActiveWaveLine[], orderId: string | null): ActiveWaveLine[] =>
    orderId == null ? [] : lines.filter(l => l.order_id === orderId);
